/**
 * Internal Demo-School Creation API Contract
 *
 * POST /internal/schools/demo  (checkApiKeyMiddleware — internal, not the
 * public `/v1/schools` route)
 *
 * Creates a School row with NO Blackbaud connection: `platformEnvironmentId`
 * and `dataLookupConnectionId` null, `platformSyncEnabled = false`, and
 * `School.platform` carrying the demo-class marker (KTD2 of plan
 * 2026-07-27-004). `createSchool` cannot serve this — it *requires* Blackbaud
 * environment ids — and no other write path may set the marker, which is what
 * makes "demo-class" a tenancy fact rather than a column anyone can flip.
 *
 * The idempotency key is the SLUG, not the name: two demo schools may share a
 * display name, and the slug is what the app's school lookup resolves.
 */

import { z } from 'zod'
import { SlackChannelIdSchema, SlackChannelNameSchema } from '../../schemas/entities/school'

/**
 * The two `School.platform` values that mark a row as demo-class.
 *
 * SINGLE DEFINITION ON PURPOSE. Three call sites depend on this list agreeing:
 * the create endpoint that mints these rows, the ordinary school-update guard
 * that refuses them, and (later PRs) the dev-login allowlist and the copier's
 * destination guard. A second hand-written copy of the strings is exactly how
 * the "only one endpoint can set this" invariant would quietly stop holding.
 */
export const DEMO_CLASS_PLATFORM_VALUES = ['demo', 'demo-template'] as const

export const DemoClassPlatformSchema = z.enum(DEMO_CLASS_PLATFORM_VALUES)
export type DemoClassPlatform = z.infer<typeof DemoClassPlatformSchema>

/** True iff `value` is one of the demo-class `School.platform` markers. */
export function isDemoClassPlatform(value: unknown): value is DemoClassPlatform {
  return typeof value === 'string' && (DEMO_CLASS_PLATFORM_VALUES as readonly string[]).includes(value)
}

/**
 * Which demo-class school to create. `demo` is the presenter-facing school
 * (initialized, surfaced by the dev-login picker and the public calendar);
 * `template` is the never-demoed pristine baseline the copier reads from.
 *
 * The request says `template` while the column says `demo-template` because the
 * caller names the ROLE and the column namespaces the marker inside the
 * `platform` value space it shares with `blackbaud`.
 */
export const DemoSchoolKindSchema = z.enum(['demo', 'template'])
export type DemoSchoolKind = z.infer<typeof DemoSchoolKindSchema>

/** The `School.platform` marker each request `kind` writes. */
export const DEMO_SCHOOL_KIND_TO_PLATFORM: Record<DemoSchoolKind, DemoClassPlatform> = {
  demo: 'demo',
  template: 'demo-template'
}

/**
 * A slug beginning with `<digits>.` is UNADDRESSABLE, not merely ugly.
 * `validateSchoolById` strips that prefix before the slug lookup so EAS build
 * ids like `0026.st-marks-school-of-texas` resolve to the real school — which
 * means a school actually STORED as `0026.demo` could never be found by its own
 * slug. Refusing it at creation is the only place that is cheap to fix.
 */
const NUMERIC_PREFIXED_SLUG = /^\d+\./

/**
 * The slug charset `createSchoolSlug` produces: lowercase alphanumerics and
 * hyphens. Kept strict so a hand-supplied demo slug cannot introduce a
 * character (a dot, a slash, whitespace) that no generated slug ever contains.
 */
const SCHOOL_SLUG_CHARSET = /^[a-z0-9-]+$/

/**
 * Request body. `name`, `slug` and `ianaTimeZone` are capped at 191 because
 * those `School` columns are uncapped Prisma `String`s, which MySQL
 * materialises as `varchar(191)` — a longer value passes validation and then
 * fails at the write.
 */
export const CreateDemoSchoolRequestSchema = z.object({
  name: z.string().trim().min(1).max(191).describe('School display name'),
  slug: z
    .string()
    .trim()
    .min(1)
    .max(191)
    .refine(value => !NUMERIC_PREFIXED_SLUG.test(value), {
      message:
        'Slug must not start with a numeric "<digits>." prefix: school lookups strip that prefix (EAS build ids), so such a school could never be resolved by its own slug.'
    })
    .refine(value => SCHOOL_SLUG_CHARSET.test(value), {
      message: 'Slug must contain only lowercase letters, digits and hyphens.'
    })
    .describe('URL slug; the idempotency key for this endpoint'),
  ianaTimeZone: z.string().trim().min(1).max(191).describe('IANA time zone, e.g. America/Chicago'),
  kind: DemoSchoolKindSchema.describe('demo = presenter-facing school; template = pristine copier source'),
  initialize: z
    .boolean()
    .default(false)
    .describe('Run initializeSchool with every Blackbaud option off, setting initializedAt')
})
export type CreateDemoSchoolRequest = z.infer<typeof CreateDemoSchoolRequestSchema>

/**
 * Response. `internalId` is the `School.id` BigInt rendered as a decimal string
 * — BigInt is not JSON-serializable, and the operator runbook needs the internal
 * id for the copier and the seed scripts, which address rows by it.
 *
 * `created` and `initialized` report what this CALL did, not what the row looks
 * like: a repeat call against an existing slug returns `created: false` and
 * `initialized: false` even though the row it names is initialized. That is what
 * makes the idempotency contract observable to the operator running it twice.
 */
export const CreateDemoSchoolResponseSchema = z.object({
  publicId: z.string().describe('School public ID (cuid)'),
  internalId: z.string().describe('School.id as a decimal string (BigInt is not JSON-serializable)'),
  platform: DemoClassPlatformSchema.describe('The demo-class marker written to School.platform'),
  created: z.boolean().describe('False when an existing row with this slug was returned unchanged'),
  initialized: z.boolean().describe('True only when THIS call ran initializeSchool')
})
export type CreateDemoSchoolResponse = z.infer<typeof CreateDemoSchoolResponseSchema>

// ============================================================================
// Seat a demo persona — POST /internal/schools/demo/members
// ============================================================================

/**
 * The ONLY `School.platform` value the presenter-facing surfaces accept.
 *
 * Deliberately NOT `isDemoClassPlatform`: that predicate covers BOTH demo-class
 * markers, and the template must never surface to a presenter or receive a
 * seated persona (KTD5). Anchored to {@link DEMO_SCHOOL_KIND_TO_PLATFORM} rather
 * than a fresh `'demo'` literal so this and the create endpoint cannot drift.
 */
export const PRESENTER_FACING_DEMO_PLATFORM: DemoClassPlatform = DEMO_SCHOOL_KIND_TO_PLATFORM.demo

/**
 * Seat a Clerk-backed demo persona at the demo school (plan 2026-07-27-004, U5).
 *
 * A persona is the small named cast a presenter signs in as (R2). It is NOT a
 * roster row: KTD13 keeps the ~1,400 copied roster rows as platform-only shells
 * with `userId` null and no account at all, and only the cast gets a `User`.
 *
 * `status` is not in the body on purpose — a seated persona is always ACTIVE.
 * Making it a knob would let an operator seat a persona that cannot sign in and
 * discover it in front of a prospect.
 */
export const SeatDemoSchoolMemberRequestSchema = z
  .object({
    schoolId: z.string().trim().min(1).max(191).describe('Target School public id; must carry platform = "demo"'),
    /**
     * Clerk user id. Required for a persona that will actually be DEMOED: the
     * dev-login mint path resolves its target through `authProviderId` and
     * refuses anything that does not start with `user_`, so a persona seated
     * without one exists but can never be picked. Validated here rather than
     * discovered at the picker.
     */
    clerkUserId: z
      .string()
      .trim()
      .min(1)
      .max(191)
      .refine(value => value.startsWith('user_'), {
        message: 'clerkUserId must be a Clerk user id (starts with "user_"); dev-login mints only for those.'
      })
      .optional(),
    email: z.string().trim().toLowerCase().email().max(191).optional(),
    firstName: z.string().trim().min(1).max(191),
    lastName: z.string().trim().min(1).max(191),
    /**
     * Stable per-school identifier. Optional; the endpoint derives one when it
     * is absent. It cannot be left NULL: `validateUsersOnSchoolsById` — the
     * resolver behind the dev-login mint route — rejects a membership whose
     * `platformId` is null, so a persona without one is unmintable.
     */
    platformId: z.string().trim().min(1).max(191).optional(),
    isFaculty: z.boolean().default(false),
    isStudent: z.boolean().default(false),
    isParent: z.boolean().default(false)
  })
  .refine(input => input.clerkUserId !== undefined || input.email !== undefined, {
    message: 'Supply at least one of clerkUserId or email to identify the persona.'
  })
  .refine(input => input.isFaculty || input.isStudent || input.isParent, {
    message: 'Set at least one role flag: a persona with no role renders on no surface.'
  })
export type SeatDemoSchoolMemberRequest = z.infer<typeof SeatDemoSchoolMemberRequestSchema>

/**
 * Response. `userCreated` / `membershipCreated` report what THIS call did, so a
 * repeat run is visibly idempotent (both false) — the same contract the demo
 * school creation endpoint uses for `created`.
 *
 * `schoolUserPublicId` is the identifier the dev-login picker sends to the mint
 * route, so the operator's runbook can go straight from this response to a
 * sign-in check without a second lookup.
 */
export const SeatDemoSchoolMemberResponseSchema = z.object({
  schoolUserPublicId: z.string().describe('UsersOnSchools public id — the dev-login mint-token path param'),
  userPublicId: z.string().describe('User public id'),
  schoolId: z.string().describe('Target school public id'),
  platformId: z.string().describe('The stable per-school identifier written to UsersOnSchools.platformId'),
  clerkUserId: z.string().nullable().describe('Null means the persona has no Clerk identity and cannot be minted'),
  userCreated: z.boolean().describe('True only when THIS call inserted the User row'),
  membershipCreated: z.boolean().describe('True only when THIS call inserted the UsersOnSchools row')
})
export type SeatDemoSchoolMemberResponse = z.infer<typeof SeatDemoSchoolMemberResponseSchema>

// ============================================================================
// Set the Slack channel pointer — PATCH /internal/schools/:id/slack-channel
// ============================================================================

/**
 * The internal sibling of the super-admin half of `PATCH /v1/schools/:id/settings`
 * (SCH-1583 / SCH-1585).
 *
 * WHY A SECOND WRITE PATH FOR A COLUMN THAT ALREADY HAS ONE. The public
 * endpoint's Slack keys are gated on a super-admin CLERK SESSION, and an agent
 * has no browser session to carry — agent-assisted writes in this repo go
 * through `/internal/*` with `x-api-key`. Eight real Slack channels have to be
 * written to eight schools by an agent, in production, and the public route
 * cannot be reached to do it.
 *
 * It is a second DOOR, not a second implementation: the route delegates to
 * `updateSchoolSettingsUseCase`, so absent-vs-null, the paste normalisation and
 * the `D…` refusal are the same code in both lanes. What differs is only how the
 * super-admin gate is cleared — by the injected system principal rather than by
 * a session claim — and the gate itself is left standing.
 */

/**
 * Request body. Both keys optional and nullable, carrying the same three-way
 * distinction the public endpoint encodes: ABSENT leaves the column untouched,
 * `null` is an explicit clear, and a value is a write.
 *
 * AT LEAST ONE KEY IS REQUIRED. An empty body would otherwise be a successful
 * no-op — and so would a body that misspelled `slackChannelId`, since Zod strips
 * unknown keys. An operator running eight of these by hand must not be able to
 * typo a key and read the 200 as "the channel is wired up". Refusing an
 * effectively-empty body is what makes that mistake visible at the moment it is
 * made. A caller who only wants to READ the pointer has `GET /v1/schools/:id`.
 */
export const SetSchoolSlackChannelRequestSchema = z
  .object({
    slackChannelId: SlackChannelIdSchema.nullable()
      .optional()
      .describe('Slack channel ID (the truth); null clears it, absent leaves it untouched'),
    slackChannelName: SlackChannelNameSchema.nullable()
      .optional()
      .describe('Cached display name; null clears it, absent leaves it untouched')
  })
  .refine(input => input.slackChannelId !== undefined || input.slackChannelName !== undefined, {
    message:
      'Send at least one of slackChannelId or slackChannelName (use null to clear). An empty body would write nothing and still answer 200.'
  })
export type SetSchoolSlackChannelRequest = z.infer<typeof SetSchoolSlackChannelRequestSchema>

/**
 * Response — the pointer as it stands AFTER the write, read back from the row
 * rather than echoed from the request, so an operator verifies the write from
 * the response instead of running a second lookup.
 *
 * `slug` and `name` are carried for the same reason: the path accepts an
 * internal id, a slug or a public id, and an operator working a list of eight
 * schools needs the answer to name the school it actually landed on.
 */
export const SetSchoolSlackChannelResponseSchema = z.object({
  schoolId: z.string().describe('School public id (cuid) — the resolved target, whatever the path named'),
  slug: z
    .string()
    .describe('School slug, so the operator can confirm which school was written; empty when the school has none'),
  name: z.string().describe('School display name'),
  slackChannelId: z.string().nullable().describe('The stored pointer after this call; null means no channel'),
  slackChannelName: z.string().nullable().describe('The stored display name after this call'),
  updatedAt: z.string().describe('School.updatedAt as an ISO-8601 string')
})
export type SetSchoolSlackChannelResponse = z.infer<typeof SetSchoolSlackChannelResponseSchema>
